/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useState} from 'react';
import {InputAdornment, Popover} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import ChevronRight from '@material-ui/icons/ChevronRight';
import classnames from 'classnames';
import isEqual from 'lodash.isequal';
import isUndefined from 'lodash.isundefined';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import {CTALink} from '../components/buttons/CTALink';
import {PrimaryButton} from '../components/buttons/PrimaryButton';
import ClickAwayListener from '../components/listeners/ClickAwayListener';
import TextField from '../components/textfield/TextField';
import Tooltip from '../components/tooltip/Tooltip';
import {DropdownArrowIcon} from '../icons/DropdownArrowIcon';
import {StarsFillIcon} from '../icons/StarsFillIcon';
import {StarsIcon} from '../icons/StarsIcon';
import {IonSnackbar} from '../notifications/IonSnackbar';
import {UserNotificationDialog} from '../notifications/UserNotificationDialog';
import {dlsAlt, dlsColor, dlsTypography} from '../theme/theme';
import {DLSTheme} from '../types/types';
import {getFilterName, hasSelectionInFilters} from './FilterHelpers';

/**
 * Defines the prop definitions for SaveFilter...
 *
 * appliedSaveFilter      : applied save filter name
 * filterState            : current filter state
 * savedPreferences       : user's prefered save filters
 * onSavedFilterSelection : invokes when the preference selected
 * onFilterSave           : invokes when the new filter saved or modified
 * disabled               : indicates that the save filter is disabled or not
 * isMobileView           : indicates the mobile view
 */
export interface SaveFilterProps {
  appliedSaveFilter: string;
  filterState: GenericObject;
  savedPreferences: GenericObject;
  onSavedFilterSelection: Function;
  onFilterSave: Function;
  disabled: boolean;
  isMobileView: boolean;
};

// defines the border style...
const border = (color) => `1px solid ${dlsColor(color)}`;

// defines the under line style...
const getUnderLineStyles = (borderColor = 'aaBlue') => ({
  borderBottom : `2px solid ${dlsColor(borderColor)} !important`
});

// defines the style definition for the SaveFilter...
const useStyles = makeStyles((theme: DLSTheme) => {
  const {
    dls: {
      motion: {
        element: {
          largeExpansion: {
            close: expansionClose,
            open: expansionOpen
          }
        }
      }
    }
  } = theme;

  return ({
    saveFilterWrapper : {
      width           : ({isMobileView}: any) => isMobileView ? '92vw' : '100%',
      backgroundColor : 'transparent !important',
      opacity         : ({disabled}: any) => disabled ? 0.6 : 1,
      pointerEvents   : ({disabled}: any) => disabled ? 'none' : 'all'
    },
    filterHeader : {
      display         : 'flex',
      alignItems      : 'center',
      justifyContent  : 'space-between',
      cursor          : 'pointer',
      padding         : '10px',
      height          : '28px',
      backgroundColor : dlsColor('white')
    },
    active : {
      borderBottom : border('caBlue')
    },
    inactive : {
      borderBottom : border('grey5')
    },
    header : {
      ...dlsAlt('components', 'dropdown'),
      color         : dlsColor('grey3'),
      fontWeight    : 'bold !important',
      fontSize      : '14px !important',
      letterSpacing : '1px',
      display       : 'flex',
      alignItems    : 'center',
      gap           : '10px',
      position      : 'relative'
    },
    iconWrapper : {
      position : 'absolute',
      left     : 0
    },
    listIconWrapper : {
      position : 'absolute',
      left     : 8,
      top      : 8
    },
    icon : {
      cursor : 'pointer'
    },
    titleWrapper : {
      overflow     : 'hidden',
      maxWidth     : '110px',
      whiteSpace   : 'pre',
      textOverflow : 'ellipsis',
      marginLeft   : '30px',
      padding      : '4px'
    },
    title : {
      display : 'inline'
    },
    savedTitle : {
      width     : '96%',
      padding   : '6px',
      margin    : '2px',
      '&:hover' : {
        color           : dlsColor('white'),
        backgroundColor : dlsColor('lightBlue')
      }
    },
    carrotWrapper : {
      paddingTop : '5px',
      cursor     : 'pointer'
    },
    filterClose : {
      transition : `transform ${expansionClose}`
    },
    filterOpen : {
      transform  : ({isMobileView}: any) => `rotateZ(${isMobileView ? 90 : 180}deg)`,
      transition : `transform ${expansionOpen}`
    },
    saveNew : {
      display         : 'flex',
      flexFlow        : 'column',
      width           : ({isMobileView}: any) => isMobileView ? 'auto' : '240px',
      backgroundColor : dlsColor('white')
    },
    savedFilter : {
      flexFlow : 'column',
      width    : ({isMobileView}: any) => isMobileView ? '88%' : '220px',
      padding  : '12px',
      cursor   : 'pointer',
      margin   : '2px'
    },
    input : {
      paddingLeft : '5px !important'
    },
    underline : {
      '&::before' : {
        ...getUnderLineStyles('grey3')
      },
      '&::after' : {
        ...getUnderLineStyles()
      },
      '&:hover' : {
        '&::before, &::after' : {
          ...getUnderLineStyles(),
          transition : 'none'
        }
      }
    },
    save : {
      fontSize      : '12px',
      lineHeight    : '16px',
      fontFamily    : 'roboto',
      fontStyle     : 'normal',
      fontWeight    : 700,
      letterSpacing : '1px',
      textTransform : 'uppercase',
      cursor        : 'pointer',
      display       : 'flex',
      alignItems    : 'center',
      color         : dlsColor('aaBlue')
    },
    textField : {
      margin : '30px 18px 57px 18px',
      width  : '80%'
    },
    saveButton : {
      margin : '0px 18px 16px 14px',
      width  : '87%'
    },
    selected : {
      color           : dlsColor('white'),
      backgroundColor : dlsColor('lightBlue')
    },
    remove : {
      ...dlsTypography('videoAddress'),
      color         : dlsColor('white'),
      fontWeight    : 400,
      letterSpacing : '0.4px',
      alignItems    : 'center'
    },
    divider : {
      borderBottom : `1px solid ${dlsColor('grey4')}`,
      margin       : '8px 0'
    },
    changed : {
      fontStyle : 'italic',
      color     : dlsColor('caBlue')
    },
    mobileWrapper : {
      width    : '93vw',
      position : 'relative'
    },
    disabled : {
      color         : dlsColor('black'),
      opacity       : 0.6,
      pointerEvents : 'none'
    }
  });
});

// defines the style definition for tooltip...
const iconsTooltip = makeStyles((theme) => ({
  arrow : {
    color : dlsColor('grey1')
  },
  tooltip : {
    ...dlsAlt('components', 'iconTooltip')
  }
}));

// defines the custom tooltip component...
function IconCustomTooltip(props) {
  return <Tooltip arrow classes={iconsTooltip()} {...props} />;
}

/**
 * Defines the Saved Filter component. This is a reusable component and can be pluggable
 * into the component where all the individual filter components are wrapped together. This
 * is tightly dependent on the passed filter state and user preferred filter. This can be used
 * for desktop and mobile.
 *
 * ```typescript
 *
 * import SaveFilter from '@ion/dls/src/filters';
 *
 * <SaveFilter isMobileView={isPhone}
 *  appliedSaveFilter={appliedSaveFilter}
 *  filterState={filterState}
 *  savedPreferences={savedPreferences}
 *  onFilterSave={onFilterSave}
 *  disabled={isDisabled}
 *  onSavedFilterSelection={onSavedFilterSelection} />
 *
 * ```
 */
export const SaveFilter = ({
  appliedSaveFilter,
  filterState,
  savedPreferences,
  onSavedFilterSelection,
  onFilterSave,
  disabled = false,
  isMobileView = false
}: SaveFilterProps) => {
  const classes                             = useStyles({disabled, isMobileView});
  const [isActive, setIsActive]             = useState(false);
  const [openNewForm, setOpenNewForm]       = useState(false);
  const [value, setValue]                   = useState(null);
  const [selected, setSelected]             = useState(null);
  const [filterToRemove, setFilterToRemove] = useState(null);
  const [message, setMessage]               = useState(null);
  const hasSavedFilters                     = Object.keys(savedPreferences)?.length > 0;
  const isAbsoluteSaveFilter                = isEqual(filterState, savedPreferences[selected]);

  useEffect(() => {
    if (!isUndefined(appliedSaveFilter) && appliedSaveFilter !== selected) {
      setSelected(appliedSaveFilter);
    }
  }, [appliedSaveFilter]);

  // invokes when the popup is closed...
  const handleClose = useCallback(function(active = isActive) {
    setIsActive(!active);
    setOpenNewForm(false);
    setValue(null);
    setFilterToRemove(null);
  }, [isActive, openNewForm, value, filterToRemove]);

  // invokes when the any saved preference selected from save filter
  const handleSavedSelection = function(filter, fromUndo = false) {
    const isCurrentFilter = filter === selected;
    const msg = fromUndo ? translate('nav:filters.undoSuccess') : translate('nav:filters.applySuccess', {filter});
    if (!fromUndo && isCurrentFilter) {
      onFilterSave({isRemove: true});
      setSelected(null);
    } else {
      onSavedFilterSelection(filter, savedPreferences[filter]);
      setSelected(filter);
      handleClose();
      setMessage(msg);
    }
  };

  // invokes when the filter has been removed from preferences...
  const handleRemoveFromSaved = function(filter) {
    const preference      = {...savedPreferences};
    const isCurrentFilter = filter === selected;
    delete preference[filter];
    onFilterSave({state: preference, isRemove: isCurrentFilter});
    isCurrentFilter && setSelected(null);
    setIsActive(false);
    setMessage(`${filter} ${translate('nav:filters.removeSuccess')}`);
  };

  // invokes for new filter save, remove and modification of exisiting save filter...
  const handleSaveNewFilter = function() {
    onFilterSave({state: savedPreferences, filterKey: value, filter: filterState});
    onSavedFilterSelection(value, filterState);
    setSelected(value);
    setMessage(translate('common:editors.shared.successCreated', {entity: value}));
    handleClose();
  };

  // unset the message from state when the snakbar is closed...
  const onMessageClose = function() {
    setMessage(null);
  };

  const renderStarIcon = (name?: string, isList = false) => {
    return (
      <>
        <IconCustomTooltip disableHoverListener={!name && !selected}
          title={
            <span className={classes.remove}>
              {translate('nav:filters.removeFromSaved')}
            </span>}>
          <span className={classes.icon}
            onClick={function(e) {
              (isList || selected) && e.stopPropagation();
              setFilterToRemove(name || selected);
              setIsActive(false);
            }
            }>
            {selected && selected === name && (isAbsoluteSaveFilter ? (
              <StarsFillIcon color={dlsColor('caBlue')} />
            ) : (
              <StarsIcon color={dlsColor('caBlue')} />
            ))}
            {(!selected || selected !== name) && (Object.keys(savedPreferences).length && !isList? (
              <StarsFillIcon color={dlsColor('black')} />
            ): (
              <StarsIcon color={dlsColor('black')} />
            ))}
          </span>
        </IconCustomTooltip>
      </>
    );
  };

  const renderTitle = (name?: string, isList = false) => {
    return (
      <>
        <span className={isList ? classes.listIconWrapper : classes.iconWrapper}>{renderStarIcon(name, isList)}</span>
        <div className={classes.titleWrapper}>
          <span className={classes.title}>
            {name ? getFilterName(name) : getFilterName(selected, hasSavedFilters ? savedPreferences : null)}
          </span>
        </div>
      </>
    );
  };

  const renderSaveCurrentFilter = () => {
    return (
      <div className={classes.saveNew}>
        <TextField className={classes.textField}
          placeholder={translate('nav:filters.newDesktop')}
          multiline
          autoFocus
          value={value}
          InputProps={{
            classes : {
              input     : classes.input,
              underline : classes.underline
            },
            ...(isMobileView ? {
              endAdornment :
                <InputAdornment position='end'>
                  <CTALink className={classnames(classes.save, !value ? classes.disabled : null)}
                    onClick={handleSaveNewFilter}>
                    {translate('nav:filters.saveText')}
                  </CTALink>
                </InputAdornment>
            } : {})
          }}
          onChange={function({target: {value}}) {
            setValue(value);
          }} />
        {!isMobileView && (
          <PrimaryButton fullWidth
            disabled={!value}
            className={classes.saveButton}
            onClick={handleSaveNewFilter}>
            {translate('nav:filters.saveText')}
          </PrimaryButton>
        )}
      </div>
    );
  };

  const renderSavedFilters = () => {
    return (
      <div className={classnames(classes.saveNew, classes.savedFilter)}>
        {hasSavedFilters && hasSelectionInFilters(filterState) &&
         (selected ? !isAbsoluteSaveFilter : true) && (
          <span className={classnames(classes.header, classes.savedTitle)}
            onClick={function() { setOpenNewForm(true); }}>
            {translate('nav:filters.expanded.create')}
          </span>
        )}
        {selected && !isAbsoluteSaveFilter && (
          <>
            <span className={classnames(classes.header, classes.savedTitle)}
              onClick={function() {
                onFilterSave({state: savedPreferences, filterKey: selected, filter: filterState});
                setMessage(translate('nav:filters.saveSuccess'));
                handleClose();
              }}>
              {translate('nav:filters.save')}
            </span>
            <span className={classnames(classes.header, classes.savedTitle)}
              onClick={function() { handleSavedSelection(selected, true); }}>
              {translate('nav:filters.undo')}
            </span>
            <span className={classes.divider} />
          </>
        )}
        {Object.keys(savedPreferences).map((filter, index: number) => (
          <span key={`${filter.split(' ').join('-')}-${index}`}
            className={classnames(
              classes.header,
              classes.savedTitle, filter === selected ? classes.selected : null)}
            onClick={function() { handleSavedSelection(filter); }}>
            {renderTitle(filter, true)}
          </span>
        ))}
      </div>
    );
  };

  return (
    <>
      <div className={classes.saveFilterWrapper}>
        <div className={classnames(classes.filterHeader, `${isActive ? classes.active : classes.inactive}`)}
          onClick={function() { handleClose(); }}>
          <div className={classnames(classes.header,
            selected && !isAbsoluteSaveFilter ? classes.changed : null)}>
            {renderTitle(selected)}
          </div>
          <span className={classnames(classes.carrotWrapper,
            isActive ? classes.filterOpen : classes.filterClose)}>
            { isMobileView ? (<ChevronRight color={dlsColor('grey5')} />) : (<DropdownArrowIcon />)}
          </span>
        </div>
        {isActive && (
          !isMobileView ? (
            <Popover open
              style={{marginLeft: '10px', borderTop: `1px solid ${dlsColor('grey4')}`}}
              anchorReference='anchorPosition'
              anchorPosition={{top: 128, left: (window.innerWidth)}}
              anchorOrigin={{
                vertical   : 'top',
                horizontal : 'left'
              }}
              transformOrigin={{
                vertical   : 'top',
                horizontal : 'left'
              }}>
              <ClickAwayListener onClickAway={handleClose}
                mountListener>
                {(!hasSavedFilters || openNewForm)? renderSaveCurrentFilter() : renderSavedFilters()}
              </ClickAwayListener>
            </Popover>
          ): (
            <div className={classes.mobileWrapper}>
              {(!hasSavedFilters || openNewForm)? renderSaveCurrentFilter() : renderSavedFilters()}
            </div>
          ))}
      </div>
      {filterToRemove && (
        <UserNotificationDialog show
          title={translate('nav:filters.remove')}
          message={`${translate('nav:filters.confirm', {filterToRemove})}`}
          actionLabel={translate('nav:filters.yes')}
          dismissLabel={translate('nav:filters.no')}
          onBackdropClick={function() {
            handleClose(true);
          }}
          onDismiss={function() {
            handleClose(true);
          }}
          onAction={function() {
            handleRemoveFromSaved(filterToRemove);
            handleClose(true);
          }} />
      )}
      {!!message && (
        <IonSnackbar show
          message={message}
          actionLabel={translate('common:editors.delete.ok')}
          onAction={onMessageClose}
          onClose={onMessageClose}
          autoHideDuration={3000} />
      )}
    </>
  );
};

export default SaveFilter;
